/*
 * Paddle Finder Kiosk - quiz engine (assets/paddle-kiosk-quiz.js)
 *
 * This is the SAME questions, scoring, tie-breaking and ranking as the online
 * quiz in sections/paddle-finder-quiz.liquid, so a customer gets the same
 * recommendations on the kiosk and on the website.
 *
 * If you change the online quiz, change this file to match and run:
 *   node scripts/kiosk/quiz-parity.test.js
 * The test reads the online quiz's code and fails if the two ever disagree.
 *
 * Plain ES5 on purpose: LG webOS and other kiosk browsers can run old Chromium.
 */
(function (root, factory) {
  if (typeof module === "object" && module.exports) {
    module.exports = factory();
  } else {
    root.SMPKioskQuiz = factory();
  }
})(this, function () {
  "use strict";

  // ---- Copied verbatim from sections/paddle-finder-quiz.liquid ----
  var QUESTIONS = [
    {
      text: "How do you like to play?",
      options: [
        "I like to attack from the back of the court with hard drives.",
        "I play all over the court and want to feel comfortable anywhere.",
        "I like quick back-and-forth at the net: blocking and hitting back fast shots.",
        "I care most about hitting the ball cleanly and keeping it in play."
      ],
      scoring: [
        { shape_elongated: 2 },
        { shape_hybrid: 2 },
        { shape_widebody: 2, shape_hybrid: 1 },
        { shape_widebody: 2 }
      ]
    },
    {
      text: "When the ball comes at you fast, what usually happens?",
      options: [
        "I hit the middle of the paddle most of the time.",
        "I make good contact, but a little extra help is nice.",
        "I sometimes hit the ball near the edge of the paddle.",
        "Fast shots are hard for me. I want as much help as I can get."
      ],
      scoring: [
        { shape_elongated: 2 },
        { shape_hybrid: 2 },
        { shape_widebody: 2, shape_hybrid: 1 },
        { shape_widebody: 3 }
      ]
    },
    {
      text: "What do you want most from your paddle?",
      options: [
        "Quick pop for fast shots at the net.",
        "A crisp feel that stays the same for a long time.",
        "The ball stays on the paddle a little longer, so I can aim my shots.",
        "A springy feel with lots of power that still feels controlled."
      ],
      scoring: [
        { core_foam: 2 },
        { core_foam: 2 },
        { core_gen3: 2 },
        { core_gen3: 2 }
      ]
    },
    {
      text: "How do you like to win points?",
      options: [
        "With lots of spin and sharp angles.",
        "With a mix of spin, placement and speed.",
        "Mostly with speed, hitting where my opponents aren't.",
        "I keep it simple. I just want to keep the ball in play."
      ],
      scoring: [
        { grit_high: 2 },
        { grit_high: 1, grit_standard: 1 },
        { grit_standard: 2 },
        { grit_standard: 2 }
      ]
    },
    {
      text: "What should the paddle feel like when you hit the ball?",
      options: [
        "Fast and firm, so I can react quickly and speed the ball up.",
        "In between: quick, but not too stiff.",
        "Softer, for drop shots, soft resets and placing the ball.",
        "Steady and controlled, for long rallies."
      ],
      scoring: [
        { thickness_14mm: 2 },
        { thickness_14mm: 1, thickness_16mm: 1 },
        { thickness_16mm: 2 },
        { thickness_16mm: 2 }
      ]
    },
    {
      text: "What kind of power do you want?",
      options: [
        "Quick pop on short swings, like fast shots at the net.",
        "A balance: quick at the net, with power on bigger swings.",
        "More power on drives, serves and overheads.",
        "As much power as possible on big, full swings."
      ],
      scoring: [
        { response_pop: 2 },
        { response_balanced: 2 },
        { response_power: 2 },
        { response_power: 3 }
      ]
    }
  ];

  var TIE_ORDER = {
    shape: ["shape_hybrid", "shape_widebody", "shape_elongated"],
    core: ["core_foam", "core_gen3"],
    grit: ["grit_high", "grit_standard"],
    thickness: ["thickness_16mm", "thickness_14mm"],
    response: ["response_balanced", "response_pop", "response_power"]
  };

  var CATEGORY_TAGS = {
    shape: ["shape_elongated", "shape_hybrid", "shape_widebody"],
    core: ["core_foam", "core_gen3"],
    grit: ["grit_standard", "grit_high"],
    thickness: ["thickness_14mm", "thickness_16mm"],
    response: ["response_pop", "response_balanced", "response_power"]
  };

  var TAG_PHRASES = {
    shape_elongated: "extended reach and attacking leverage",
    shape_hybrid: "a balanced all-court shape",
    shape_widebody: "a larger, more forgiving hitting area",
    core_foam: "quick pop and a crisp, durable response",
    core_gen3: "added dwell time, power, and control",
    grit_high: "strong spin and shot-shaping potential",
    grit_standard: "a predictable surface focused on placement and consistency",
    thickness_14mm: "a faster and more direct contact feel",
    thickness_16mm: "a softer, more controlled contact feel",
    response_pop: "fast counters, flicks, and speedups",
    response_balanced: "a blend of kitchen speed and full-swing performance",
    response_power: "heavier drives, serves, and overheads"
  };

  var CATEGORY_WEIGHTS = { shape: 5, core: 5, thickness: 3, response: 3, grit: 2 };
  var CATEGORY_ORDER = ["shape", "core", "response", "thickness", "grit"];

  var TITLE_PRIORITY = ["perseus", "hyperion", "agassi", "graf"];
  // ---- End of copied definitions ----

  function priorityRank(title) {
    var lower = (title || "").toLowerCase();
    for (var i = 0; i < TITLE_PRIORITY.length; i++) {
      if (lower.indexOf(TITLE_PRIORITY[i]) !== -1) return i;
    }
    return TITLE_PRIORITY.length;
  }

  function computeCategoryScores(answers) {
    var totals = {};
    Object.keys(CATEGORY_TAGS).forEach(function (cat) {
      CATEGORY_TAGS[cat].forEach(function (tag) { totals[tag] = 0; });
    });
    answers.forEach(function (optIndex, qIdx) {
      var pts = QUESTIONS[qIdx].scoring[optIndex] || {};
      Object.keys(pts).forEach(function (tag) {
        totals[tag] = (totals[tag] || 0) + pts[tag];
      });
    });
    return totals;
  }

  function pickWinner(totals, category) {
    var tags = CATEGORY_TAGS[category];
    var best = tags[0];
    tags.forEach(function (tag) { if (totals[tag] > totals[best]) best = tag; });
    var bestScore = totals[best];
    var tied = tags.filter(function (tag) { return totals[tag] === bestScore; });
    if (tied.length > 1) {
      var order = TIE_ORDER[category];
      for (var i = 0; i < order.length; i++) {
        if (tied.indexOf(order[i]) !== -1) return order[i];
      }
    }
    return best;
  }

  function pickWinners(answers) {
    var totals = computeCategoryScores(answers);
    var winners = {};
    Object.keys(CATEGORY_TAGS).forEach(function (cat) { winners[cat] = pickWinner(totals, cat); });
    return winners;
  }

  function scoreProduct(product, winners) {
    var breakdown = {};
    var score = 0;
    var tags = product.tags || [];
    ["shape", "core", "thickness", "response", "grit"].forEach(function (cat) {
      var match = tags.indexOf(winners[cat]) !== -1;
      breakdown[cat] = match;
      if (match) score += CATEGORY_WEIGHTS[cat];
    });
    return { score: score, breakdown: breakdown };
  }

  function scoreAllProducts(products, winners, minScore) {
    var scored = products
      .map(function (p) {
        var result = scoreProduct(p, winners);
        return { product: p, score: result.score, breakdown: result.breakdown };
      })
      .filter(function (r) { return r.score >= minScore; });

    scored.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      for (var i = 0; i < CATEGORY_ORDER.length; i++) {
        var cat = CATEGORY_ORDER[i];
        var aMatch = a.breakdown[cat] ? 1 : 0;
        var bMatch = b.breakdown[cat] ? 1 : 0;
        if (bMatch !== aMatch) return bMatch - aMatch;
      }
      if (b.product.price !== a.product.price) return b.product.price - a.product.price;
      if (a.product.createdAt !== b.product.createdAt) {
        return new Date(b.product.createdAt) - new Date(a.product.createdAt);
      }
      var aPriority = priorityRank(a.product.title);
      var bPriority = priorityRank(b.product.title);
      if (aPriority !== bPriority) return aPriority - bPriority;
      return a.product.title.localeCompare(b.product.title);
    });

    return scored;
  }

  // Same-spec products that also tie on price and date added (e.g. colorways)
  // are equally valid "Best Match" picks, exactly as on the website.
  function getTieGroup(scored) {
    if (scored.length === 0) return [];
    var top = scored[0];
    return scored.filter(function (r) {
      if (r.score !== top.score) return false;
      if (r.product.price !== top.product.price) return false;
      if (r.product.createdAt !== top.product.createdAt) return false;
      for (var i = 0; i < CATEGORY_ORDER.length; i++) {
        if (!!r.breakdown[CATEGORY_ORDER[i]] !== !!top.breakdown[CATEGORY_ORDER[i]]) return false;
      }
      return true;
    });
  }

  function attachMatchedTags(entry, winners) {
    var matchedTags = {};
    Object.keys(entry.breakdown).forEach(function (cat) {
      if (entry.breakdown[cat]) matchedTags[cat] = winners[cat];
    });
    entry.breakdown.matchedTags = matchedTags;
    return entry;
  }

  function explainMatch(breakdown) {
    var phrases = [];
    CATEGORY_ORDER.forEach(function (cat) {
      if (breakdown.matchedTags && breakdown.matchedTags[cat]) {
        phrases.push(TAG_PHRASES[breakdown.matchedTags[cat]]);
      }
    });
    if (phrases.length === 0) return "A solid option from our current lineup.";
    if (phrases.length === 1) return "A good match for players who want " + phrases[0] + ".";
    var last = phrases.pop();
    return "A strong match for players who want " + phrases.join(", ") + ", and " + last + ".";
  }

  /*
   * Runs the full recommendation exactly like the website's finishQuiz() +
   * renderResults(): score, sort, take the first `maxRecommendations`, drop
   * repeated titles inside that slice, and label Best / Strong / Alternative.
   * `clearWinner` is the paddle the website would redirect straight to.
   */
  function recommend(answers, products, options) {
    var opts = options || {};
    var minScore = typeof opts.minScore === "number" ? opts.minScore : 10;
    var max = typeof opts.maxRecommendations === "number" ? opts.maxRecommendations : 3;
    var winners = pickWinners(answers);
    var scored = scoreAllProducts(products, winners, minScore).map(function (e) {
      return attachMatchedTags(e, winners);
    });
    var tieGroup = getTieGroup(scored);
    var tieCount = tieGroup.length;
    var clearWinner = (scored.length > 0 && tieCount <= 1) ? scored[0] : null;

    var seenTitles = {};
    var picks = [];
    scored.slice(0, max).forEach(function (entry, i) {
      if (seenTitles[entry.product.title]) return;
      seenTitles[entry.product.title] = true;
      var label = i < tieCount ? "Best Match" : (i === tieCount ? "Strong Match" : "Alternative Match");
      picks.push({
        product: entry.product,
        score: entry.score,
        maxScore: maxScore(),
        breakdown: entry.breakdown,
        label: label,
        best: i < tieCount,
        explanation: explainMatch(entry.breakdown)
      });
    });

    return { winners: winners, scored: scored, tieCount: tieCount, clearWinner: clearWinner, picks: picks };
  }

  function maxScore() {
    var total = 0;
    Object.keys(CATEGORY_WEIGHTS).forEach(function (k) { total += CATEGORY_WEIGHTS[k]; });
    return total;
  }

  return {
    QUESTIONS: QUESTIONS,
    TIE_ORDER: TIE_ORDER,
    CATEGORY_TAGS: CATEGORY_TAGS,
    CATEGORY_ORDER: CATEGORY_ORDER,
    CATEGORY_WEIGHTS: CATEGORY_WEIGHTS,
    TAG_PHRASES: TAG_PHRASES,
    TITLE_PRIORITY: TITLE_PRIORITY,
    computeCategoryScores: computeCategoryScores,
    pickWinner: pickWinner,
    pickWinners: pickWinners,
    scoreProduct: scoreProduct,
    scoreAllProducts: scoreAllProducts,
    getTieGroup: getTieGroup,
    explainMatch: explainMatch,
    recommend: recommend,
    maxScore: maxScore
  };
});
